import { clipsInSections, type MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { overwriteClip, type MakeClipId } from './clipOps';

/** True for a chorus of the song map. */
export function isChorus(section: Pick<MusicVideoSongMapSection, 'kind'>): boolean {
  return section.kind.toLowerCase().includes('chorus') && !section.kind.toLowerCase().includes('pre');
}

/**
 * "Cut length · 2 · 4 · 8 beats" of the recut box (design c6), with no AI: the section gets a cut
 * on every `beatsPerCut`-th beat from its start, and the footage of its clips takes turns in the
 * new shots, in their order. The kept clips stay where they were. Pure.
 */
export function resliceSection(
  doc: MusicVideoDoc,
  section: Pick<MusicVideoSongMapSection, 'index' | 'startSec' | 'endSec'>,
  beats: readonly number[],
  beatsPerCut: number,
  makeId: MakeClipId
): MusicVideoDoc {
  const inside = doc.clips.filter((clip) => clip.endSec > section.startSec && clip.startSec < section.endSec);
  const step = Math.max(1, Math.round(beatsPerCut));
  if (inside.length === 0) {
    return doc;
  }
  const sectionBeats = beats.filter((sec) => sec >= section.startSec - 0.001 && sec < section.endSec - 0.001);
  const cuts = [section.startSec, ...sectionBeats.filter((sec, position) => position > 0 && position % step === 0)];
  const kept = inside.filter((clip) => clip.keep);
  let next = doc;
  cuts.forEach((startSec, position) => {
    const endSec = cuts[position + 1] ?? section.endSec;
    const model = inside[position % inside.length];
    if (model !== undefined && endSec > startSec) {
      next = overwriteClip(next, { ...model, id: makeId(), startSec, endSec, keep: false }, makeId);
    }
  });
  for (const clip of kept) {
    next = overwriteClip(next, clip, makeId);
  }
  return next;
}

/**
 * "Use this cut for every chorus" (design c6, f2): the clips of the section `sourceIndex` go into
 * every other chorus at the same places from its start. A longer chorus repeats the pattern, a
 * shorter one cuts it. The kept clips of a chorus ("Keep on recut") stay where they were. Pure.
 */
export function copySectionCutToChoruses(
  doc: MusicVideoDoc,
  sections: readonly MusicVideoSongMapSection[],
  sourceIndex: number,
  makeId: MakeClipId
): MusicVideoDoc {
  const source = sections.find((section) => section.index === sourceIndex);
  if (source === undefined) {
    return doc;
  }
  const sourceLength = source.endSec - source.startSec;
  const pattern = doc.clips
    .filter((clip) => clip.endSec > source.startSec && clip.startSec < source.endSec)
    .map((clip) => ({
      clip,
      offsetSec: Math.max(0, clip.startSec - source.startSec),
      lengthSec: Math.min(clip.endSec, source.endSec) - Math.max(clip.startSec, source.startSec),
      inSec: clip.inSec + Math.max(0, source.startSec - clip.startSec) * clip.speed,
    }));
  if (pattern.length === 0 || sourceLength <= 0) {
    return doc;
  }
  const targets = sections.filter((section) => section.index !== sourceIndex && isChorus(section));
  let next = doc;
  for (const target of targets) {
    const kept = clipsInSections(next, [target], [target.index]).filter((clip) => clip.keep);
    const targetLength = target.endSec - target.startSec;
    for (let loopStart = 0; loopStart < targetLength; loopStart += sourceLength) {
      for (const piece of pattern) {
        const startSec = target.startSec + loopStart + piece.offsetSec;
        const endSec = Math.min(target.endSec, startSec + piece.lengthSec);
        if (startSec < target.endSec && endSec > startSec) {
          next = overwriteClip(next, { ...piece.clip, id: makeId(), startSec, endSec, inSec: piece.inSec }, makeId);
        }
      }
    }
    // The kept clips win: they go back on top, at their own place.
    for (const clip of kept) {
      next = overwriteClip(next, clip, makeId);
    }
  }
  return next;
}
